/**
 * Trophy strip surfaced on artist profiles — one row per contest
 * placement (1st/2nd/3rd) pulled from the public
 * `/hall-of-champions/user/{user_id}` endpoint. Self-hides when the
 * user has no wins.
 *
 * NOTE: This endpoint is always available (a user's trophy list is a
 * fact once won) — independent of the Contest Center feature flag.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ShowMoreGrid from "@/components/ShowMoreGrid";

const positionLabel = (p) => (p === 1 ? "🥇 1st" : p === 2 ? "🥈 2nd" : p === 3 ? "🥉 3rd" : `#${p}`);
const positionTone = (p) =>
  p === 1
    ? "bg-amber-500/20 border-amber-500/60 text-amber-100"
    : p === 2
      ? "bg-slate-400/20 border-slate-400/60 text-slate-100"
      : "bg-orange-600/20 border-orange-500/60 text-orange-100";

export const ProfileContestWins = ({ userId }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    (async () => {
      try {
        const r = await api.get(`/hall-of-champions/user/${userId}`);
        if (cancelled) return;
        setRows(r.data?.champions || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (rows == null) return null;
  if (rows.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-900/15 via-black to-black p-4 sm:p-5"
      data-testid="profile-contest-wins"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-white font-black text-lg flex items-center gap-2">
          🏆 Contest Wins <span className="text-xs text-gray-500 font-normal">({rows.length})</span>
        </h3>
        <Link
          to="/hall-of-champions"
          className="text-xs text-amber-300 hover:text-amber-200 underline"
          data-testid="profile-contest-wins-hof"
        >
          Hall of Champions →
        </Link>
      </div>
      <ShowMoreGrid
        items={rows}
        initial={2}
        testIdPrefix="profile-contest-wins"
        gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
      >
        {(r) => (
          <Link
            key={r.hof_id}
            to={r.contest?.slug ? `/contests/${r.contest.slug}` : "/hall-of-champions"}
            className="rounded-xl border border-white/10 bg-black/50 hover:bg-black/70 hover:border-amber-500/40 transition-all p-3 flex items-center gap-3"
            data-testid={`profile-contest-win-${r.hof_id}`}
          >
            <span
              className={`inline-flex items-center justify-center rounded-full border font-black text-xs px-2.5 py-1 ${positionTone(r.position)}`}
            >
              {positionLabel(r.position)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-black truncate">
                {r.contest?.title || r.contest_title || "Contest"}
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {r.entry_snapshot?.title || r.entry_ref_id}
                {r.awarded_at && ` · ${new Date(r.awarded_at).getFullYear()}`}
              </p>
            </div>
          </Link>
        )}
      </ShowMoreGrid>
    </section>
  );
};

export default ProfileContestWins;
